/* This file is the source for our public-facing Sidekick API documentation!
If you touch this file please tag Integrations PM on your PR and manually test our 
sidekick docs locally by running `make api-doc`, then open web/build/api-docs/index.html 
in the browser to play around */

import appEvents from 'events/app';
import applyDefaultConfiguration from 'applyDefaultConfiguration';
import appEnums from 'enums/app';
import checkCustomerUrlUpdated from 'actions/session/checkCustomerUrlUpdated';
import clearCurrentRule from 'actions/app/clearCurrentRule';
import configureApp from 'configuration/configureApp';
import configureDemo from 'configuration/configureDemo';
import configSelectors from 'selectors/configuration';
import evaluateRules from 'actions/configuration/evaluateRules';
import { gladlyApiShow } from 'actions/gladlyapi/show';
import isMobileWidth from 'lib/isMobileWidth';
import { isThrottled } from 'app/modules';
import minimizeWidget from 'actions/ui/minimizeWidget';
import reconnectChatClient from 'actions/app/reconnectChatClient';
import startApp from 'configuration/startApp';
import { AVAILABILITY, AVAILABILITY_CHANGE_EVENT } from './GladlySidekickConstants';

/**
 * @namespace Gladly
 * @description
 *
 * The Gladly Sidekick is an embeddable UI element that lives on your website and allows customers to search for
 * answers or talk and interact with your team in real-time. The Gladly Sidekick API enables you to have programmatic
 * control of the Gladly Sidekick.
 *
 *
 *
 * ### JavaScript snippet
 * Add the following JavaScript embed code to all your site’s pages, preferably at the bottom of the page to avoid
 * slowing down the main page content to load. This will load the code that powers the Gladly Sidekick on your site.
 *
 *
 * Note that the fourth argument of the snippet below specifies 'PROD' which enables you to use the latest release of
 * the Chat-SDK with your production appId.
 *
 * ```
 *  <script>
 *   !function(c,n,r,t){if(!c[r]){var i,d,p=[];d="PROD"!==t&&t?"STAGING"===t?"https://cdn.gladly.qa/gladly/chat-sdk/widget.js":t:"https://cdn.gladly.com/chat-sdk/widget.js",c[r]={init:function(){i=arguments;var e={then:function(t){return p.push({type:"t",next:t}),e},catch:function(t){return p.push({type:"c",next:t}),e}};return e}},c.__onHelpAppHostReady__=function(t){if(delete c.__onHelpAppHostReady__,(c[r]=t).loaderCdn=d,i)for(var e=t.init.apply(t,i),n=0;n<p.length;n++){var a=p[n];e="t"===a.type?e.then(a.next):e.catch(a.next)}},function(){try{var t=n.getElementsByTagName("script")[0],e=n.createElement("script");e.async=!0,e.src=d+"?q="+(new Date).getTime(),t.parentNode.insertBefore(e,t)}catch(t){}}()}}
 *   (window,document,'Gladly','PROD')
 *  </script>
 * ```
 *
 *
 * ### Initialize
 * In order to start the Gladly Sidekick on your site, you must initialize it after you load the above
 * JavaScript embed code.
 *
 * #### Basic Initialization:
 * Add the following script tag to your page to automatically initialize the Gladly Sidekick. This is
 * most appropriate if you do not want to take advantage of any Gladly API functionality and just want
 * to display a basic widget. Work with your Gladly CSM to get the “your-own-id” value.
 * ```
 * <script>
 *  window.gladlyConfig = {
 *    appId: 'your-own-id'
 *  };
 * </script>
 * ```
 *
 * #### Manual Initialization:
 * Sidekick can be manually initialized to get better control over when to display the Sidekick.
 * This could be desirable, for example, if you want to start the Sidekick from your own button.
 *
 * For a more dynamic interaction, Sidekick exposes an Availability state that allows
 * the site to change the behavior depending on if chat is closed or if it's unavailable due to high
 * demand.
 *
 * The example below initializes the Sidekick, then checks to see if chat is available and sets
 * a class on a button assumed to exist on the page.
 *
 * ```
 * Gladly.init({
 *   appId: "your-own-id"
 * }).then(function() {
 *   // This is called once Sidekick has been initialized.
 *
 *   // An element with the id "start-chat" is assumed to be on the page.
 *   var startChatButton = document.getElementById('start-chat');
 *
 *   // An onClick handler can be attached to the "start-chat" element
 *   startChatButton.onclick = function() {Gladly.show()};
 *
 *   // This API call gets the current availability
 *   var availablility = Gladly.getAvailability();
 *
 *   // Setting the availability as the class name on the "start-chat" element,
 *   // this allows us to change the look of the button via CSS.
 *   startChatButton.setAttribute('class', availablility.toLowerCase());
 *
 *   // Subscribe to changes in availability, and update the class on the "start-chat" element.
 *   Gladly.on('availability:change', function(availablility) {
 *     startChatButton.setAttribute('class', availablility.toLowerCase());
 *   });
 * }).catch(function(error) {
 *   // If anything goes wrong when Sidekick is being initialized, this gets called.
 *   console.log('error:', error)
 * });
 * ```
 *
 * ### Browser Support
 * The Gladly Sidekick supports major browsers. For the desktop, we support current + previous major
 * versions of Chrome, Firefox, and Safari along with IE11 and the current version of Microsoft Edge.
 * For mobile, we support current + previous major versions of Chrome on Android and iOS and the
 * current + previous versions of Safari on iOS.
 *
 *
 * ### Working with Sandbox / Staging Environment
 *
 * You can use our sandbox environment to test upcoming Chat-SDK releases or new configuration on your
 * staging site. To launch the Gladly Sidekick on your staging site, follow the steps above to add the
 * JavaScript embed code and initialize. Then make following modification:
 *
 * Change the value in this part of your embed code to 'STAGING':
 *
 * ```
 *     (window,document,'Gladly','STAGING')
 *```
 *
 *
 * ### Deployment Checklist
 *
 * 1. Add embed code to correct environment
 * 2. Add embed code only once to each page
 * 3. Initialize the Sidekick by either defining window.gladlyConfig OR calling Gladly.init(), never both
 *
 * ### Custom Minimized Button
 *
 * Gladly allows you to customize the minimized chat button that is rendered on your site to better align with your brand.
 * There are a few simple steps to implement a custom minimized chat button.
 *
 * 1. Gladly requires that an HTML id of `custom-gladly-chat-button` be specified on the outermost HTML element
 * 2. Gladly requires that an onclick handler be bound to the same outermost HTML element
 * 3. The onclick handler will need to call the public Gladly API `show`
 *
 * If the HTML `custom-gladly-chat-button` id is present, Gladly will do the following:
 *   1. decorate that HTML element with a class of `gladly-show`, when the button should be visible
 *   2. decorate that HTML element with a class of `gladly-has-authenticated` when a user has authenticated
 *   3. decorate that HTML element with a class of `gladly-unread` when there are unread messages
 *
 * It is up to you to utilize these classes and hooks properly to render and style your own custom button as you wish.
 *
 * #### Sample HTML
 * ```
 * <div id="custom-gladly-chat-button" onclick="Gladly.show()">
 *   <div id="gladly-not-authenticated">
 *     <span>LIVE CHAT</span>
 *   </div>

 *   <div id="gladly-authenticated">
 *     <svg width="18" height="18" fill="none" viewBox="0 0 18 18">
 *       <path fill="#000" />
 *     </svg>
 *     <span class="unread-dot"></span>
 *    </div>
 * </div>
 *```
 *
 * #### Sample CSS
 *```
 * <style type="text/css">
 *     #custom-gladly-chat-button {
 *       visibility: hidden;
 *     }
 *
 *     #custom-gladly-chat-button #gladly-not-authenticated {
 *         visibility: hidden;
 *     }
 *
 *     #custom-gladly-chat-button #gladly-authenticated {
 *         visibility: hidden;
 *     }
 *
 *     #custom-gladly-chat-button.gladly-show #gladly-not-authenticated {
 *         visibility: visible;
 *     }
 *
 *     #custom-gladly-chat-button.gladly-show.gladly-has-authenticated #gladly-authenticated {
 *         visibility: visible;
 *     }
 *
 *     #custom-gladly-chat-button.gladly-show.gladly-has-authenticated #gladly-not-authenticated {
 *         visibility: hidden;
 *     }
 *
 *     #custom-gladly-chat-button .unread-dot {
 *         visibility: hidden;
 *     }
 *
 *     #custom-gladly-chat-button.gladly-unread .unread-dot {
 *         background-color: green;
 *         border-radius: 50%;
 *         display: inline;
 *         height: 8px;
 *         position: absolute;
 *         right: 12px;
 *         top: 8px;
 *         visibility: visible;
 *         width: 8px;
 *     }
 * </style>
 * ```
 *
 * ### Content Security Policy
 * [Content-Security-Policy](https://www.w3.org/TR/CSP2/) (CSP) is an advanced added layer of security that helps detect and
 * mitigate certain types of attacks, including Cross Site Scripting (XSS) and data injection attacks.
 * For websites that make use of CSP the following directives has to be added to make Sidekick work as expected:
 *
 * #### Production Environment
 * ```
 * default-src blob: 'unsafe-inline' https://*.gladly.com https://d1fc8wv8zag5ca.cloudfront.net https://*.smooch.io https://js.verygoodvault.com;
 * connect-src https://*.gladly.com https://*.smooch.io wss://*.smooch.io https://*.sinter-collect.com https://*.verygoodproxy.com;
 * ```
 *
 * #### Sandbox / Staging Environment
 * ```
 * default-src blob: 'unsafe-inline' https://*.gladly.qa https://*.gladly.com https://d1fc8wv8zag5ca.cloudfront.net https://*.smooch.io https://js.verygoodvault.com;
 * connect-src https://*.gladly.qa https://*.smooch.io wss://*.smooch.io https://*.sinter-collect.com https://*.verygoodproxy.com;
 * ```
 */

/**
 * Reference to Redux store.
 * @ignore
 */
var store;
var observers;

/**
 * @memberof Gladly
 * @typedef {Object} SidekickConfig
 * @property {string} appId - id specifying your own application
 */

/**
 * @memberof Gladly
 * @func init
 *
 * @description This method initializes the Sidekick on your site using the ID specified in the config parameter.
 * It returns a promise that will resolve when the Sidekick is ready.
 *
 * Note that `init` must be called, and the initialization must fully complete (which is signaled when the returned
 * promise is resolved) before calling any other api methods.
 *
 * @param {Gladly.SidekickConfig} config - Sidekick configuration
 * @returns {Promise}
 */
export async function init(config, deviceConfig) {
  const isCrawler = /lebo|awle|pide|obo|rawli|dsbo/i.test(navigator.userAgent);
  if (isCrawler) {
    return;
  }
  config = applyDefaultConfiguration(config);
  const shouldConfigureDemo =
    (config.demoSelfServiceEnabled || config.widgetType === 'DEMO_SELF_SERVICE') && config.demoOrgId && config.demoUrl;

  let configuration = shouldConfigureDemo
    ? await configureDemo(config.cdn, config, deviceConfig)
    : await configureApp(config.cdn, config, deviceConfig);

  store = configuration.store;
  observers = configuration.observers;
  startApp({ store });
  stageChangeListener(store, observers);
  store.dispatch(appEvents.appStarted());
}

/**
 * @memberof Gladly
 * @func getAvailability
 *
 * @description This method returns the current chat [Availability]{@linkcode Gladly.Availability}
 *
 * @returns {Gladly.Availability}
 */
export function getAvailability() {
  const state = store.getState();
  const isWithinOfficeHours = configSelectors.getWithinOfficeHours(state.configuration);

  if (!isWithinOfficeHours) {
    return AVAILABILITY.UNAVAILABLE_OFFICE_CLOSED;
  }
  if (isThrottled(state)) {
    return AVAILABILITY.UNAVAILABLE_BUSY;
  }
  return AVAILABILITY.AVAILABLE;
}

/**
 * @memberof Gladly
 * @func on
 *
 * @description This method subscribes an observer to eventName and returns an unsubscribe function.
 *
 * @param {String} eventName - The name of the event to subscribe to
 * @param {Function} observer - Observer function to be invoked when event triggers
 *
 * @returns {Function} - The function to remove the newly added observer function
 */
export function on(eventName, func) {
  observers.addObserver(eventName, func);
  return () => {
    observers.removeObservers(eventName, func);
  };
}

/**
 * @memberof Gladly
 *
 * @func show
 * @description This method displays the Gladly Sidekick when called.
 *
 * #### Example:
 * ```
 * <button onClick="Gladly.show()">Start Chat</button>
 * ```
 */
export function show() {
  store.dispatch(gladlyApiShow());
}

/**
 * @memberof Gladly
 *
 * @func navigate
 * @description This method is used to notify the Sidekick when your website navigates
 * without reloading the page. Gladly will read the updated location from
 * 'document.location.href'.
 *
 * Most common use for this would be if you have
 * a single page application and you have rules configured to control what pages
 * the Sidekick is displayed on.
 *
 * #### Example:
 * ```
 * Gladly.navigate();
 * ```
 */
export function navigate() {
  if (store.getState().app.platform === appEnums.platforms.WEB && isMobileWidth()) {
    store.dispatch(minimizeWidget);
  }

  store.dispatch(clearCurrentRule);
  store.dispatch(evaluateRules);
  store.dispatch(checkCustomerUrlUpdated);
}

/**
 * This method is not a public facing API and is intended to be internally used
 * to reconnect to the chat service.
 *
 * iOS SDK will use this to proactively ensure there is an active connection
 * every time the web view is loaded. This is an optimization so consumers
 * won't have to manually reconnect each time there is a disconnect
 * @ignore
 */
export function reconnect() {
  store.dispatch(reconnectChatClient);
}

function stageChangeListener(store, observers) {
  let currentStage = getAvailability();
  store.subscribe(() => {
    let previousStage = currentStage;
    currentStage = getAvailability();
    if (currentStage != previousStage) {
      observers.getObservers(AVAILABILITY_CHANGE_EVENT).forEach(observer => observer(currentStage));
    }
  });
}
